import React, { useEffect, useState, useRef } from 'react';

/**
 * Lightweight fade transition wrapper.
 * Fades children in when they mount or when `show` changes to true.
 * Uses CSS transitions for performance — no external animation library needed.
 */
export function FadeIn({
  children,
  show = true,
  duration = 300,
  delay = 0,
  className = '',
}: {
  children: React.ReactNode;
  show?: boolean;
  duration?: number;
  delay?: number;
  className?: string;
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => setVisible(true), delay);
      return () => clearTimeout(timer);
    } else {
      setVisible(false);
    }
  }, [show, delay]);

  return (
    <div
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transition: `opacity ${duration}ms ease-in-out`,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Slide-in transition from a given direction.
 * Uses CSS transforms for smooth, GPU-accelerated animations.
 */
export function SlideIn({
  children,
  show = true,
  direction = 'right',
  duration = 300,
  delay = 0,
  className = '',
}: {
  children: React.ReactNode;
  show?: boolean;
  direction?: 'left' | 'right' | 'up' | 'down';
  duration?: number;
  delay?: number;
  className?: string;
}) {
  const [visible, setVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(show);

  useEffect(() => {
    if (show) {
      setShouldRender(true);
      const timer = setTimeout(() => setVisible(true), Math.max(delay, 10));
      return () => clearTimeout(timer);
    } else {
      setVisible(false);
      const timer = setTimeout(() => setShouldRender(false), duration);
      return () => clearTimeout(timer);
    }
  }, [show, delay, duration]);

  if (!shouldRender) return null;

  const transforms: Record<string, string> = {
    left: 'translateX(-100%)',
    right: 'translateX(100%)',
    up: 'translateY(-100%)',
    down: 'translateY(100%)',
  };

  return (
    <div
      className={className}
      style={{
        transform: visible ? 'translate(0, 0)' : transforms[direction],
        opacity: visible ? 1 : 0,
        transition: `transform ${duration}ms ease-out, opacity ${duration}ms ease-out`,
        willChange: 'transform, opacity',
      }}
    >
      {children}
    </div>
  );
}

/**
 * Wrapper that fades content in on tab/panel change.
 * Useful for ClientPortal tab transitions.
 */
export function TabFade({
  children,
  activeKey,
  duration = 200,
  className = '',
}: {
  children: React.ReactNode;
  activeKey: string | number;
  duration?: number;
  className?: string;
}) {
  const [visible, setVisible] = useState(true);
  const prevKey = useRef(activeKey);

  useEffect(() => {
    if (prevKey.current !== activeKey) {
      setVisible(false);
      // Wait for fade-out to complete before updating the key and fading back in
      const timer = setTimeout(() => {
        prevKey.current = activeKey;
        setVisible(true);
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [activeKey, duration]);

  return (
    <div
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transition: `opacity ${duration}ms ease-in-out`,
      }}
    >
      {children}
    </div>
  );
}
